import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  toRoundedReadableNumber,
  percentOfBigNumber,
  toInternationalCurrencySystemLongString,
  toPrecision,
  multiply,
  ONLY_ZEROS,
  scientificNotationToString,
} from '../../utils/numbers';
import { TokenMetadata } from '../../services/ft-contract';
import { TokenBalancesView } from '../../services/token';
import { IconLeftV3 } from '../tokens/Icon';
import { InputAmountV3 } from './InputAmount';
import SelectToken, { SelectTokenDCL } from './SelectToken';
import { FormattedMessage } from 'react-intl';
import { useClientMobile } from '../../utils/device';
import { SWAP_MODE } from '../../pages/SwapPage';
import { WRAP_NEAR_CONTRACT_ID } from '../../services/wrap-near';
import Big from 'big.js';
import { regularizedPrice } from '../../services/swapV3';
import {
  RefreshIcon,
  LockIcon,
  ReduceIcon,
  PlusIcon,
  LockInIcon,
} from '../icon/swapV3';
import BigNumber from 'bignumber.js';
import { WalletContext } from '../../utils/wallets-integration';
import { toRealSymbol } from '../../utils/token';
import CustomTooltip from 'src/components/customTooltip/customTooltip';

interface TokenAmountProps {
  amount?: string;
  max?: string;
  total?: string;
  tokens?: TokenMetadata[];
  showSelectToken?: boolean;
  selectedToken: TokenMetadata;
  balances?: TokenBalancesView;
  onMax?: (input: HTMLInputElement) => void;
  onSelectToken?: (token: TokenMetadata) => void;
  onSearchToken?: (value: string) => void;
  onChangeAmount?: (amount: string) => void;
  text?: string;
  disabled?: boolean;
  useNearBalance?: boolean;
  forSwap?: boolean;
  isError?: boolean;
  tokenPriceList?: Record<string, any>;
  swapMode?: SWAP_MODE;
  preSelected?: TokenMetadata;
  postSelected?: TokenMetadata;
  onSelectPost?: (token: TokenMetadata) => void;
  onSelectPre?: (token: TokenMetadata) => void;
  forWrap?: boolean;
  showQuickButton?: boolean;
  ExtraElement?: JSX.Element;
  forLimitOrder?: boolean;
  marketPriceLimitOrder?: string;
  limitOrderDisable?: boolean;
  curRate?: string;
  onChangeRate?: (rate: string) => void;
  tokenIn?: TokenMetadata;
  tokenOut?: TokenMetadata;
  onBlur?: (event?: any) => void;
  limitFee?: number;
  setDiff?: (priceDifference: string) => void;
  allowWNEAR?: boolean;
  forCross?: boolean;
  nearErrorTip?: JSX.Element;
  isOut?: boolean;
}

function getTextWidth(text: string, fontSize: string) {
  let result = 10;
  const measureElement = document.createElement('span');

  measureElement.innerText = text;
  measureElement.style.fontSize = fontSize;

  (document.body || document.documentElement).append(measureElement);

  result = measureElement.offsetWidth;

  (document.body || document.documentElement).removeChild(measureElement);
  return result;
}

export function TokenAmountV3({
  amount,
  max,
  total,
  tokens,
  selectedToken,
  balances,
  onSelectToken,
  onSearchToken,
  onChangeAmount,
  forCross,
  text,
  showSelectToken = true,
  disabled = false,
  useNearBalance,
  forSwap,
  isError,
  tokenPriceList,
  forLimitOrder,
  tokenIn,
  tokenOut,
  onBlur,
  swapMode,
  preSelected,
  postSelected,
  onSelectPost,
  onSelectPre,
  forWrap,
  ExtraElement,
  marketPriceLimitOrder,
  limitOrderDisable,
  onChangeRate,
  curRate,
  limitFee,
  setDiff,
  allowWNEAR,
  isOut,
  nearErrorTip,
}: TokenAmountProps) {
  const { globalState } = useContext(WalletContext);
  const isSignedIn = globalState.isSignedIn;
  const [isOverOneLine, setIsOverOneLine] = useState(false);
  const render = (token: TokenMetadata) =>
    toRoundedReadableNumber({
      decimals: token.decimals,
      number: balances ? balances[token.id] : '0',
    });

  const [hoverSelectToken, setHoverSelectToken] = useState<boolean>(false);

  const tokenPrice = tokenPriceList?.[selectedToken?.id]?.price || null;

  const curMax =
    selectedToken?.id === WRAP_NEAR_CONTRACT_ID &&
    selectedToken?.symbol === 'NEAR' &&
    !forWrap
      ? Number(max) <= 0.2
        ? '0'
        : toPrecision(
            scientificNotationToString(
              new BigNumber(max).minus(0.2).toString()
            ),
            12
          )
      : max;

  const priceFivePercentAbove =
    tokenIn &&
    tokenOut &&
    limitFee &&
    toPrecision(
      regularizedPrice(
        percentOfBigNumber(105, marketPriceLimitOrder || 0, 8),
        tokenIn,
        tokenOut,
        limitFee
      ),
      8
    );
  const priceTenPercentAbove =
    tokenIn &&
    tokenOut &&
    limitFee &&
    toPrecision(
      regularizedPrice(
        percentOfBigNumber(110, marketPriceLimitOrder || 0, 8),
        tokenIn,
        tokenOut,
        limitFee
      ),
      8
    );
  const rateDiff = new Big(curRate || '0')
    .minus(marketPriceLimitOrder || '0')
    .div(marketPriceLimitOrder || '1')
    .times(100);

  const displayRateDiff =
    (Number(rateDiff) < 0 ? '-' : Number(rateDiff) > 0 ? '+' : '') +
    (Number(curRate) === Number(priceFivePercentAbove)
      ? 5
      : Number(curRate) === Number(priceTenPercentAbove)
      ? 10
      : Math.abs(Number(rateDiff)) < 1
      ? toPrecision(
          scientificNotationToString(
            rateDiff.times(rateDiff.lt(0) ? -1 : 1).toString()
          ),
          1
        )
      : rateDiff.times(rateDiff.lt(0) ? -1 : 1).toFixed(0));
  useEffect(() => {
    if (setDiff) {
      setDiff(displayRateDiff);
    }
  }, [displayRateDiff]);
  const showRateDiff =
    forLimitOrder &&
    !ONLY_ZEROS.test(amount) &&
    Number(rateDiff) !== 0 &&
    !ONLY_ZEROS.test(curRate) &&
    marketPriceLimitOrder &&
    swapMode === SWAP_MODE.LIMIT;

  function RateDiffDOM_newline(props: any) {
    const { over } = props;
    return (
      <div
        className={`relative -top-2 px-1 flex items-center ${
          over ? '' : 'hidden'
        }`}
      >
        <span
          className={`rounded-xl ${
            Number(displayRateDiff) > 0
              ? 'text-gradientFrom bg-gradientFrom '
              : Number(displayRateDiff) <= -10
              ? 'text-error bg-error'
              : 'text-warn bg-warn'
          }  py-0.5 px-2 bg-opacity-20 mr-1 ${showRateDiff ? '' : 'hidden'}`}
        >
          {Number(displayRateDiff) > 1000
            ? '>1000'
            : Number(displayRateDiff) < -1000
            ? '<-1000'
            : displayRateDiff}
          %
        </span>
        {ExtraElement}
      </div>
    );
  }

  function RateDiffDOM(props: any) {
    const [left, setLeft] = useState(0);
    const { over, setOver } = props;
    useEffect(() => {
      const textWidth = getTextWidth(amount, '20px') + 10;
      const inputWidth =
        document.getElementById('rateDiffInput')?.offsetWidth || 0;
      const spanWidth =
        document.getElementById('rateDiffSpan')?.offsetWidth || 0;
      const divWidth = document.getElementById('rateDiffDiv')?.offsetWidth || 0;
      const inputAreaWidth = Math.min(textWidth, inputWidth);
      const isOver = inputAreaWidth + spanWidth > divWidth;

      setLeft(inputAreaWidth);
      setOver(isOver);
    }, [amount, showRateDiff]);

    return (
      <div
        id="rateDiffSpan"
        className={`flex items-center absolute ${
          over ? 'invisible' : 'visible'
        }`}
        style={{
          left: left + 'px',
        }}
      >
        <span
          className={`rounded-xl ${
            Number(displayRateDiff) > 0
              ? 'text-gradientFrom bg-gradientFrom '
              : Number(displayRateDiff) <= -10
              ? 'text-error bg-error'
              : 'text-warn bg-warn'
          }  py-0.5 px-2 bg-opacity-20 mr-1 ${showRateDiff ? '' : 'hidden'}`}
        >
          {Number(displayRateDiff) > 1000
            ? '>1000'
            : Number(displayRateDiff) < -1000
            ? '<-1000'
            : displayRateDiff}
          %
        </span>
        {ExtraElement}
      </div>
    );
  }

  const usdLabel =
    !!tokenPrice && !ONLY_ZEROS.test(amount) && !isError && !limitOrderDisable
      ? '~ $' +
        toInternationalCurrencySystemLongString(
          multiply(tokenPrice || '0', amount || '0'),
          2
        )
      : '';

  const zearToken = selectedToken && (
    <div className="flex items-center flex-shrink-0">
      {selectedToken.icon ? (
        <img
          src={selectedToken.icon}
          alt=""
          className="rounded-full"
          style={{ width: 28, height: 28 }}
        />
      ) : null}
      <span className="ml-2 text-white text-base font-medium">
        {toRealSymbol(selectedToken.symbol)}
      </span>
      <svg
        className="ml-1.5"
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
      >
        <path
          d="M1 1l4 4 4-4"
          stroke="#C5CED4"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );

  if (forSwap && swapMode !== SWAP_MODE.LIMIT) {
    return (
      <div
        className="relative text-xs"
        style={{
          background: 'rgba(14, 20, 38, 0.86)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: '14px 16px 12px',
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm text-white">{isOut ? 'Buy' : 'Sell'}</span>
          <span style={{ color: '#8B969E' }}>
            <FormattedMessage id="balance" defaultMessage="Balance" />
            {': '}
            <span
              className={
                onChangeAmount && !forLimitOrder
                  ? 'cursor-pointer hover:text-white'
                  : ''
              }
              onClick={() => {
                if (onChangeAmount && !forLimitOrder) onChangeAmount(curMax);
              }}
              title={total}
            >
              {isSignedIn ? toPrecision(total, 3, true) : '-'}
            </span>
            {selectedToken?.symbol
              ? ` ${toRealSymbol(selectedToken.symbol)}`
              : ''}
          </span>
        </div>
        <div className="flex items-center justify-between mt-3">
          {showSelectToken && (
            <SelectToken
              tokenPriceList={tokenPriceList}
              tokens={tokens}
              render={render}
              customWidth
              forCross={forCross}
              selected={zearToken}
              onSelect={onSelectToken}
              balances={balances}
              allowWNEAR={allowWNEAR}
            />
          )}
          <div className="flex-1 min-w-0 ml-3">
            <InputAmountV3
              className="w-full"
              id="inputAmount"
              name={selectedToken?.id}
              max={onChangeAmount ? curMax : null}
              value={amount}
              onChangeAmount={onChangeAmount}
              disabled={disabled || !!isOut}
              forSwap
            />
            <div className="text-right" style={{ color: '#8B969E' }}>
              {usdLabel}
            </div>
          </div>
        </div>
        {nearErrorTip}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col text-xs bg-opacity-20 ${
        isOut && swapMode !== SWAP_MODE.LIMIT
          ? 'bg-cardBg border-inputV3BorderHoverColor'
          : 'bg-black'
      } bg-black rounded-2xl px-3 xsm:px-2 pt-1 pb-2.5 border border-inputV3BorderColor hover:border-inputV3BorderHoverColor`}
    >
      {text ? (
        <div className="text-limitOrderInputColor text-xs pt-1.5">{text}</div>
      ) : null}

      <fieldset className="relative flex  align-center items-center my-1.5">
        <InputAmountV3
          className="border border-transparent rounded w-full mr-2"
          id="inputAmount"
          name={selectedToken?.id}
          max={onChangeAmount ? curMax : null}
          value={limitOrderDisable ? '' : amount}
          onChangeAmount={onChangeAmount}
          forLimitOrder={limitOrderDisable}
          disabled={
            disabled ||
            limitOrderDisable ||
            (isOut && swapMode !== SWAP_MODE.LIMIT)
          }
          forSwap={!!forSwap}
          nearValidation={tokenIn && tokenIn.id === WRAP_NEAR_CONTRACT_ID}
          onBlur={(event) => {
            if (!!onBlur) {
              const newPrice = regularizedPrice(
                curRate,
                tokenIn,
                tokenOut,
                limitFee
              );

              if (ONLY_ZEROS.test(toPrecision(newPrice, 8, false, false)))
                return;

              onBlur(newPrice);
            }
          }}
          openClear={false}
          rateDiff={
            ExtraElement ? (
              <RateDiffDOM over={isOverOneLine} setOver={setIsOverOneLine} />
            ) : null
          }
        />
        {swapMode === SWAP_MODE.LIMIT ? (
          <SelectTokenDCL
            onSelect={onSelectToken}
            selectedToken={selectedToken}
            selectTokenIn={onSelectPre}
            selectTokenOut={onSelectPost}
            limitOrder={true}
          />
        ) : (
          showSelectToken && (
            <SelectToken
              tokenPriceList={tokenPriceList}
              tokens={tokens}
              render={render}
              customWidth
              forCross={forCross}
              selected={
                selectedToken && (
                  <div
                    className="flex items-center justify-end font-semibold"
                    onMouseEnter={() => setHoverSelectToken(true)}
                    onMouseLeave={() => setHoverSelectToken(false)}
                  >
                    <IconLeftV3
                      size={'7'}
                      token={selectedToken}
                      hover={hoverSelectToken}
                      className={'p-1'}
                    />
                  </div>
                )
              }
              onSelect={onSelectToken}
              balances={balances}
              allowWNEAR={allowWNEAR}
            />
          )
        )}
      </fieldset>
      {ExtraElement ? <RateDiffDOM_newline over={isOverOneLine} /> : null}
      <div className="flex items-center justify-between h-6">
        <span className="mr-3 text-limitOrderInputColor">
          {!!tokenPrice &&
          !ONLY_ZEROS.test(amount) &&
          !isError &&
          !limitOrderDisable
            ? '$' +
              toInternationalCurrencySystemLongString(
                multiply(tokenPrice || '0', amount || '0'),
                2
              )
            : '$-'}
        </span>
        <span className="text-limitOrderInputColor">
          <FormattedMessage id="balance" defaultMessage="Balance" />
          :&nbsp;
          <span
            className={`${
              !!onChangeAmount && !forLimitOrder
                ? 'hover:text-white cursor-pointer underline'
                : ''
            } `}
            onClick={() => {
              if (onChangeAmount && !forLimitOrder) {
                onChangeAmount(curMax);
              }
            }}
            title={total}
          >
            {isSignedIn ? toPrecision(total, 3, true) : '-'}
          </span>
        </span>
      </div>
      {nearErrorTip}
    </div>
  );
}

export function LimitOrderRateSetBox({
  tokenIn,
  tokenOut,
  marketPriceLimitOrder,
  onChangeRate,
  curRate,
  limitFee,
  setDiff,
  setRate,
  curPrice,
  fee,
  triggerFetch,
  hidden,
  hasLockedRate,
  setHasLockedRate,
}: any) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rateSort, setRateSort] = useState(true);
  const [amount, setAmount] = useState('');
  const [inputStatus, setInputStatus] = useState(false);
  const isMobile = useClientMobile();
  const priceOneTickUp =
    tokenIn &&
    tokenOut &&
    limitFee &&
    toPrecision(regularizedPrice(curRate, tokenIn, tokenOut, limitFee, 1), 8);

  const priceOneTickDown =
    tokenIn &&
    tokenOut &&
    limitFee &&
    toPrecision(regularizedPrice(curRate, tokenIn, tokenOut, limitFee, -1), 8);

  const [symbolsArr] = useState(['e', 'E', '+', '-']);

  function switchLockStatus() {
    setHasLockedRate(!hasLockedRate);
  }

  function getTokenASymbol() {
    if (rateSort) return tokenIn?.symbol;
    else return tokenOut?.symbol;
  }

  function getTokenBSymbol() {
    if (rateSort) return tokenOut?.symbol;
    else return tokenIn?.symbol;
  }

  const displayInputValue = useMemo(() => {
    if (!curPrice) {
      return '-';
    } else {
      if (rateSort) {
        return curRate;
      } else {
        try {
          if (inputStatus) {
            return amount;
          } else {
            const rate_reverse = new BigNumber(1).dividedBy(curRate).toFixed();
            return toPrecision(rate_reverse, 8);
          }
        } catch (error) {}
      }
    }
  }, [curPrice, curRate, rateSort, inputStatus, amount]);
  return (
    <>
      <div
        className={`flex flex-col  justify-between flex-grow bg-black bg-opacity-20 rounded-xl p-2.5 border border-inputV3BorderColor hover:border-inputV3BorderHoverColor mr-2 ${
          hidden ? 'hidden' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <RefreshIcon
              className={`text-primaryText cursor-pointer`}
              onClick={() => {
                setRateSort(!rateSort);
              }}
            />
            <span className="text-xs text-primaryText ml-2">
              1 {getTokenASymbol()} ={' '}
            </span>
          </div>
          <div className="flex items-center text-xs text-greenColor hover:text-senderHot">
            <span
              className="cursor-pointer select-none"
              onClick={() => {
                setRate(curPrice);
                if (triggerFetch) triggerFetch();
              }}
            >
              <FormattedMessage id="market_rate"></FormattedMessage>
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span
            onClick={() => {
              if (rateSort) {
                onChangeRate(priceOneTickDown);
              } else {
                onChangeRate(priceOneTickUp);
              }
            }}
            className={`flex items-center justify-center cursor-pointer w-5 h-5 rounded-md ${
              isMobile ? '' : 'hover:bg-selectTokenV3BgColor'
            } `}
          >
            <ReduceIcon></ReduceIcon>
          </span>
          <div className="flex items-center relative">
            <input
              onWheel={() => inputRef.current.blur()}
              min="0"
              ref={inputRef}
              step="any"
              type="number"
              placeholder={!curPrice ? '-' : '0.0'}
              value={displayInputValue}
              onBlur={(event) => {
                const regularizedRate = regularizedPrice(
                  curRate,
                  tokenIn,
                  tokenOut,
                  fee
                );

                if (
                  ONLY_ZEROS.test(toPrecision(regularizedRate, 8, false, false))
                ) {
                  return;
                }
                setRate(regularizedRate);
                setInputStatus(false);
              }}
              onChange={(event) => {
                const inputValue = event.target.value;
                setAmount(inputValue);
                setInputStatus(true);
                if (!curPrice) {
                  return null;
                } else {
                  if (!rateSort && +inputValue > 0) {
                    setRate(new BigNumber(1).dividedBy(inputValue).toFixed());
                  } else {
                    setRate(inputValue);
                  }
                }
              }}
              className="text-sm text-white text-center px-2"
              disabled={!curPrice}
              onKeyDown={(event) =>
                symbolsArr.includes(event.key) && event.preventDefault()
              }
            />
            <span className="text-xs text-primaryText mx-0.5">
              {getTokenBSymbol()}
            </span>
          </div>
          <div className="flex items-center">
            <span
              onClick={() => {
                if (rateSort) {
                  onChangeRate(priceOneTickUp);
                } else {
                  onChangeRate(priceOneTickDown);
                }
              }}
              className={`flex items-center justify-center cursor-pointer w-5 h-5 rounded-md ${
                isMobile ? '' : 'hover:bg-selectTokenV3BgColor'
              }`}
            >
              <PlusIcon></PlusIcon>
            </span>
            <div
              onClick={switchLockStatus}
              className={`flex items-center justify-center w-5 h-5 rounded-md ml-0.5 cursor-pointer ${
                isMobile ? '' : 'hover:bg-selectTokenV3BgColor'
              }`}
              data-tooltip-id="lockratehint"
            >
              <span>{hasLockedRate ? <LockInIcon /> : <LockIcon />}</span>
            </div>
          </div>
        </div>
      </div>

      <CustomTooltip id={'lockratehint'}>
        <div style={{ maxWidth: 240 }}>
          Lock the rate field to get your buy amount automatically adjusted when
          changing your sell amount.
        </div>
      </CustomTooltip>
    </>
  );
}
